import React from "react";
import { observer, useLocalObservable } from "mobx-react";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import RestoreFromTrashIcon from "@mui/icons-material/RestoreFromTrash";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Switch,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
  useTheme,
  Theme,
} from "@mui/material";
import {
  StyledTableCell,
  StyledTableRow,
} from "../../../../../styles/Shipmentstyles";
import { CategoryListItem } from "../../../../../models/Categories/types";
import { EmployeeRoles } from "../../../../../models/Employees/EmployeeRoles";
import { useStores } from "../../../use-stores";
import AppTableHeaderCell from "../../../../../views/components/common/pages/AppTableHeaderCell";
import AppTablePagenation from "../../../../../views/components/common/pages/AppTablePagenation";
import AppTableRowCell from "../../../../../views/components/common/pages/AppTableRowCell";
import { SortType } from "../../../../../models/SortType";
import { formatDate } from "../../../../../views/components/common/functions/formatDate";

export interface CategoryTableProps {
  maxHeight: number | string;
  categories: CategoryListItem[];
  limit: number;
  page_no: number;
  total_count: number;
  sortBy: string;
  sortType: SortType;
  onPageChange: (
    event: React.MouseEvent<HTMLButtonElement> | null,
    page: number,
  ) => void;
  onRowsPerPageChange: (limit: number) => void;
  onSortChange: (column: string, sortType: SortType) => void;
  onToggleDisplay: (category: CategoryListItem) => Promise<void> | void;
  onDelete: (category: CategoryListItem) => void;
  onRestore: (category: CategoryListItem) => void;
}

function CategoryTable({
  maxHeight,
  categories,
  page_no,
  limit,
  total_count,
  sortBy,
  sortType,
  onPageChange,
  onRowsPerPageChange,
  onSortChange,
  onToggleDisplay,
  onDelete,
  onRestore,
}: CategoryTableProps) {
  const navigate = useNavigate();
  const theme = useTheme<Theme>();
  const { userStore } = useStores();
  const canEdit = userStore.hasPermission(EmployeeRoles.EDIT_CATALOG, true);
  const colors = theme.palette.customColors;

  const ui = useLocalObservable(() => ({
    togglingDisplayIds: {} as Record<number, boolean>,
    setToggling(id: number, value: boolean) {
      this.togglingDisplayIds = { ...this.togglingDisplayIds, [id]: value };
    },
  }));

  const handleSort = (column: string) => {
    onSortChange(
      column,
      sortBy === column && sortType === SortType.DESC
        ? SortType.ASC
        : SortType.DESC,
    );
  };

  const goToDetail = (id: number) => navigate(`/categories/${id}`);

  const handleRowsChange = (value: number) => {
    onPageChange(null, 1);
    onRowsPerPageChange(value);
  };

  const handleDisplayToggle = async (row: CategoryListItem) => {
    if (!canEdit || ui.togglingDisplayIds[row.id]) return;
    ui.setToggling(row.id, true);
    try {
      await onToggleDisplay(row);
    } finally {
      ui.setToggling(row.id, false);
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{ width: "100%", maxWidth: "100%", overflow: "hidden" }}
    >
      <TableContainer
        sx={{
          maxHeight,
          maxWidth: "100%",
          overflow: "auto",
          WebkitOverflowScrolling: "touch",
        }}
      >
        <Table stickyHeader size="small" sx={{ minWidth: 720 }}>
          <TableHead>
            <TableRow>
              <AppTableHeaderCell>
                <TableSortLabel
                  active={sortBy === "name"}
                  direction={
                    sortBy === "name" && sortType === SortType.ASC
                      ? "asc"
                      : "desc"
                  }
                  onClick={() => handleSort("name")}
                >
                  Name
                </TableSortLabel>
              </AppTableHeaderCell>
              <AppTableHeaderCell>
                <TableSortLabel
                  active={sortBy === "code"}
                  direction={
                    sortBy === "code" && sortType === SortType.ASC
                      ? "asc"
                      : "desc"
                  }
                  onClick={() => handleSort("code")}
                >
                  Code
                </TableSortLabel>
              </AppTableHeaderCell>
              <AppTableHeaderCell>
                <TableSortLabel
                  active={sortBy === "priority"}
                  direction={
                    sortBy === "priority" && sortType === SortType.ASC
                      ? "asc"
                      : "desc"
                  }
                  onClick={() => handleSort("priority")}
                >
                  Priority
                </TableSortLabel>
              </AppTableHeaderCell>
              <AppTableHeaderCell>
                <TableSortLabel
                  active={sortBy === "active"}
                  direction={
                    sortBy === "active" && sortType === SortType.ASC
                      ? "asc"
                      : "desc"
                  }
                  onClick={() => handleSort("active")}
                >
                  Active
                </TableSortLabel>
              </AppTableHeaderCell>
              <AppTableHeaderCell>
                <TableSortLabel
                  active={sortBy === "display"}
                  direction={
                    sortBy === "display" && sortType === SortType.ASC
                      ? "asc"
                      : "desc"
                  }
                  onClick={() => handleSort("display")}
                >
                  Display
                </TableSortLabel>
              </AppTableHeaderCell>
              <AppTableHeaderCell>Updated</AppTableHeaderCell>
              {canEdit && (
                <AppTableHeaderCell txtAlign="right">
                  Actions
                </AppTableHeaderCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {categories.length === 0 ? (
              <StyledTableRow>
                <StyledTableCell colSpan={7} align="center">
                  <Typography
                    variant="body2"
                    color={colors?.greyTextColor}
                    sx={{ py: 4 }}
                  >
                    No categories found.
                  </Typography>
                </StyledTableCell>
              </StyledTableRow>
            ) : (
              categories.map((row) => {
                const displayLoading = Boolean(ui.togglingDisplayIds[row.id]);
                const isActive = row.active === 1;
                return (
                  <StyledTableRow
                    key={row.id}
                    hover
                    sx={{ cursor: "pointer" }}
                    onClick={() => goToDetail(row.id)}
                  >
                    <AppTableRowCell>
                      <Typography variant="body2" fontWeight={600}>
                        {row.name}
                      </Typography>
                    </AppTableRowCell>
                    <AppTableRowCell>{row.code?.trim() || "—"}</AppTableRowCell>
                    <AppTableRowCell>{row.priority ?? "—"}</AppTableRowCell>
                    <AppTableRowCell>
                      <Chip
                        size="small"
                        label={isActive ? "Active" : "Inactive"}
                        color={isActive ? "success" : "default"}
                        variant={isActive ? "filled" : "outlined"}
                        sx={{
                          height: 22,
                          fontSize: "0.65rem",
                          "& .MuiChip-label": { px: 1 },
                        }}
                      />
                    </AppTableRowCell>
                    <AppTableRowCell onClick={(e) => e.stopPropagation()}>
                      {canEdit ? (
                        <Tooltip
                          title={
                            row.display === 1
                              ? "Hide on storefront"
                              : "Show on storefront"
                          }
                        >
                          <Box
                            component="span"
                            onClick={() => void handleDisplayToggle(row)}
                            sx={{
                              display: "inline-flex",
                              alignItems: "center",
                              cursor: displayLoading ? "wait" : "pointer",
                            }}
                          >
                            {displayLoading ? (
                              <CircularProgress size={18} />
                            ) : (
                              <Switch
                                checked={row.display === 1}
                                size="small"
                                color="primary"
                                sx={{ pointerEvents: "none" }}
                              />
                            )}
                          </Box>
                        </Tooltip>
                      ) : (
                        <Chip
                          size="small"
                          label={row.display === 1 ? "Shown" : "Hidden"}
                          color={row.display === 1 ? "success" : "default"}
                          variant={row.display === 1 ? "filled" : "outlined"}
                          sx={{
                            height: 22,
                            fontSize: "0.65rem",
                            "& .MuiChip-label": { px: 1 },
                          }}
                        />
                      )}
                    </AppTableRowCell>
                    <AppTableRowCell>
                      {row.modified_date ? formatDate(row.modified_date) : "—"}
                    </AppTableRowCell>
                    <AppTableRowCell onClick={(e) => e.stopPropagation()}>
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="flex-end"
                        gap={0.5}
                      >
                        {canEdit ? (
                          isActive ? (
                            <Tooltip title="Delete category">
                              <IconButton
                                size="small"
                                onClick={() => onDelete(row)}
                                sx={{
                                  color: colors?.errorRed,
                                  border: `1px solid ${colors?.greyBorder}`,
                                  borderRadius: 1.5,
                                }}
                                aria-label={`Delete ${row.name}`}
                              >
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          ) : (
                            <Tooltip title="Restore category">
                              <IconButton
                                size="small"
                                onClick={() => onRestore(row)}
                                sx={{
                                  color:
                                    colors?.purpleMain ||
                                    theme.palette.primary.main,
                                  border: `1px solid ${colors?.greyBorder}`,
                                  borderRadius: 1.5,
                                }}
                                aria-label={`Restore ${row.name}`}
                              >
                                <RestoreFromTrashIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )
                        ) : null}
                      </Box>
                    </AppTableRowCell>
                  </StyledTableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ px: 1 }}>
        <AppTablePagenation
          totalCount={total_count}
          pageNo={page_no}
          limit={limit}
          onPageChange={onPageChange}
          handleRowsChange={handleRowsChange}
        />
      </Box>
    </Paper>
  );
}

export default observer(CategoryTable);
